import { useMemo, useEffect, useRef } from 'react';

import ACT from 'australian-postcodes/data/by-state/ACT.json';
import NSW from 'australian-postcodes/data/by-state/NSW.json';
import NT from 'australian-postcodes/data/by-state/NT.json';
import QLD from 'australian-postcodes/data/by-state/QLD.json';
import SA from 'australian-postcodes/data/by-state/SA.json';
import TAS from 'australian-postcodes/data/by-state/TAS.json';
import VIC from 'australian-postcodes/data/by-state/VIC.json';
import WA from 'australian-postcodes/data/by-state/WA.json';

const stateDataMap = { ACT, NSW, NT, QLD, SA, TAS, VIC, WA };
const allRecords = [...ACT, ...NSW, ...NT, ...QLD, ...SA, ...TAS, ...VIC, ...WA];

function uniqueValues(records, key) {
  const seen = new Set();
  return records.reduce((acc, r) => {
    if (!seen.has(r[key])) { seen.add(r[key]); acc.push(r[key]); }
    return acc;
  }, []);
}

/**
 * Cross-fills state/suburb/postcode from the australian-postcodes dataset.
 * Form-library agnostic: takes the current state value and plain setter callbacks,
 * so it can be wired into react-hook-form, Formik, or plain useState.
 */
export function useAddressAutofill({ state, setState, setSuburb, setPostcode }) {
  // Prevents the state-change effect below from clearing fields that were just auto-filled
  const isAutoFillingState = useRef(false);
  // Skips the clear-on-change effect for the mount render, so a form that mounts
  // with values already filled in (e.g. checkout re-showing a partially filled address) isn't wiped
  const isMounted = useRef(false);

  const records = useMemo(
    () => state ? (stateDataMap[state] || []) : allRecords,
    [state]
  );

  const suburbs = useMemo(() => uniqueValues(records, 'suburb'), [records]);
  const postcodes = useMemo(() => uniqueValues(records, 'postcode'), [records]);

  // Clears suburb/postcode when the user manually changes state, but not when auto-filled
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (!isAutoFillingState.current) {
      setSuburb('');
      setPostcode('');
    }
    isAutoFillingState.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const crossFillFromMatches = (matches, fillKey, setFill) => {
    const uniqueStates = [...new Set(matches.map(r => r.state))];
    if (uniqueStates.length === 1 && uniqueStates[0] !== state) {
      isAutoFillingState.current = true;
      setState(uniqueStates[0]);
    }

    const stateToUse = uniqueStates.length === 1 ? uniqueStates[0] : state;
    if (!stateToUse) return;

    const uniqueFillValues = [...new Set(matches.filter(r => r.state === stateToUse).map(r => r[fillKey]))];
    if (uniqueFillValues.length === 1) setFill(uniqueFillValues[0]);
  };

  const handleSuburbValueChange = (value) => {
    const matches = allRecords.filter(r => r.suburb === value.toUpperCase());
    if (matches.length) crossFillFromMatches(matches, 'postcode', setPostcode);
  };

  const handlePostcodeValueChange = (value) => {
    const matches = allRecords.filter(r => r.postcode === value);
    if (matches.length) crossFillFromMatches(matches, 'suburb', setSuburb);
  };

  return { suburbs, postcodes, handleSuburbValueChange, handlePostcodeValueChange };
}
